import { useEffect, useState } from 'react';
import { Button, Input, Modal, Table, Tag, notification } from 'antd';
import { highlight, languages } from 'prismjs';
import Editor from 'react-simple-code-editor';

import { useLazyGetCitationDetailQuery, useLazyGetCitationsQuery } from '../../redux/api/citations';

interface CitationRow {
    id: number;
    courtId: number | null;
    departmentId: number | null;
    incidentId: number | null;
    date: string;
    workflowType: string;
    incidentType: string;
}

const PAGE_SIZE = 25;

const formatData = (data: string): string => {
    try {
        return JSON.stringify(JSON.parse(data), null, 2);
    } catch {
        return data;
    }
};

const CitationsPage = () => {

    const [citations, setCitations] = useState<CitationRow[]>([]);
    const [page, setPage] = useState({ current: 1, pageSize: PAGE_SIZE, total: 0 });
    const [search, setSearch] = useState('');
    const [tableHeight, setTableHeight] = useState(0);
    const [isViewOpen, setViewOpen] = useState(false);
    const [viewData, setViewData] = useState('');

    const [getCitations] = useLazyGetCitationsQuery();
    const [getCitationDetail] = useLazyGetCitationDetailQuery();

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 72,
        },
        {
            title: 'Court ID',
            dataIndex: 'courtId',
            key: 'courtId',
            width: 104,
        },
        {
            title: 'Department ID',
            dataIndex: 'departmentId',
            key: 'departmentId',
            width: 128,
        },
        {
            title: 'Incident ID',
            dataIndex: 'incidentId',
            key: 'incidentId',
            width: 104,
        },
        {
            title: 'Workflow Type',
            dataIndex: 'workflowType',
            key: 'workflowType',
            width: 160,
            render: (v: string) => v ? <Tag>{v.replace(/_/g, ' ')}</Tag> : '',
        },
        {
            title: 'Incident Type',
            dataIndex: 'incidentType',
            key: 'incidentType',
            width: 136,
            render: (v: string) => v ? <Tag>{v}</Tag> : '',
        },
        {
            title: 'Date and Time Received',
            dataIndex: 'date',
            key: 'date',
            width: 200,
            render: (date: string) => date ? new Date(date + 'Z').toLocaleString() : '',
        },
        {
            title: 'Action',
            key: 'action',
            width: 96,
            render: (_: any, record: CitationRow) => (
                <Button size="small" onClick={() => onView(record)}>
                    View
                </Button>
            ),
        },
    ];

    useEffect(() => {
        const updateTableHeight = () => setTableHeight(window.innerHeight - 288);
        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);
        return () => window.removeEventListener('resize', updateTableHeight);
    }, []);

    useEffect(() => {
        fetchCitations(page.current, search);
    }, [page.current, search]);

    const fetchCitations = async (index: number, searchTerm: string) => {
        try {
            const params: any = { index, viewableSize: PAGE_SIZE };
            if (searchTerm) params.search = searchTerm;
            const data: any = await getCitations(params).unwrap();
            setCitations(data.items.map((c: CitationRow) => ({ ...c, key: c.id })));
            setPage(prev => ({ ...prev, total: data.page.totalSize }));
        } catch (e) {
            notification.error({
                message: 'Failed to load citations',
                description: 'An error occurred while fetching citations.',
            });
        }
    };

    const handleTableChange = (pagination: any) => {
        setPage(prev => ({ ...prev, current: pagination.current }));
    };

    const onSearch = (value: string) => {
        setSearch(value);
        setPage(prev => ({ ...prev, current: 1 }));
    };

    const onView = async (record: CitationRow) => {
        try {
            const detail = await getCitationDetail(record.id).unwrap();
            setViewData(formatData(detail.data));
            setViewOpen(true);
        } catch (e) {
            notification.error({
                message: 'Failed to load citation',
                description: 'An error occurred while fetching the citation data.',
            });
        }
    };

    const closeViewModal = () => {
        setViewOpen(false);
        setViewData('');
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>Incidents</h1>
                <Input.Search
                    placeholder="Search citation data"
                    onSearch={onSearch}
                    onChange={(e) => { if (!e.target.value) onSearch(''); }}
                    allowClear
                    style={{ width: 320 }}
                />
            </div>

            <Table
                dataSource={citations}
                columns={columns}
                rowKey="id"
                scroll={{ y: tableHeight }}
                pagination={{
                    current: page.current,
                    pageSize: page.pageSize,
                    total: page.total,
                }}
                onChange={handleTableChange}
            />

            <Modal
                title="Citation Data"
                closable={false}
                open={isViewOpen}
                onOk={closeViewModal}
                cancelButtonProps={{ style: { display: 'none' } }}
                width={600}
                destroyOnClose={true}
                style={{ top: 40 }}
                styles={{
                    content: { height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' },
                    body: { flex: 1, overflow: 'auto' },
                }}
            >
                <Editor
                    value={viewData}
                    highlight={(code) => highlight(code, languages.js!, 'js')}
                    padding={10}
                    contentEditable={false}
                    onValueChange={() => { }}
                    style={{
                        minHeight: '100%',
                        background: 'white',
                        border: 'none',
                        fontFamily: '"Fira code", "Fira Mono", monospace',
                        fontSize: 14,
                    }}
                />
            </Modal>
        </>
    );
};

export default CitationsPage;
